import * as yup from "yup";
import {
  commonSchema,
  directDebitSchema,
  supporterSchema,
} from "@cruk/fundraising-data-validations";
import { isUkCountry } from "../helpers/functions/shared";
import {
  nameRegExp,
  andRegExp,
  nameExcludedRegExp,
  cardholderNameExcludedRegExp,
  phoneRegExp,
  postcodeUkRegExp,
  postcodeIntlRegExp,
  extraEmailRegExp,
  sortCodeRegExp,
} from "../helpers/regexp";
import { Option } from "../helpers/types/shared";
import "../helpers/yupMethods";

const getRequiredMessage = (hasNudges: boolean) =>
  `Please ${
    hasNudges ? "select or " : ""
  }type in the amount you'd like to donate.`;

const donationAmountSchema = (
  frequency: Option<string>,
  formName: string,
  formVersion: string,
) => {
  const minAmount =
    formVersion === "regular" && formName === "support-us" ? 3 : 2;
  let max: number;

  switch (frequency) {
    case "Monthly":
      max = 850;
      break;
    case "Quarterly":
      max = 2500;
      break;
    case "Annually":
      max = 10000;
      break;
    default:
      max = 100000;
  }

  const base = {
    amountRadioGroup: yup.mixed().when("otherAmount", {
      is: "" || undefined,
      then: yup
        .string()
        .when("$hasNudges", (hasNudges: boolean, schema) =>
          schema.required(getRequiredMessage(hasNudges)),
        ),
      otherwise: yup.string(),
    }),
    otherAmount: yup.mixed().when("amountRadioGroup", {
      is: "" || undefined,
      then: yup
        .string()
        .matches(
          commonSchema.amount.allowedCharacters.regex,
          commonSchema.amount.allowedCharacters.errorMessage,
        )
        .test(
          "min",
          `Please enter an amount of £${minAmount} or greater.`,
          (userInput) => !userInput || Number(userInput) >= minAmount,
        )
        .test(
          "max",
          formVersion === "regular"
            ? "Thank you for your generous donation. So that we’re able to thank you appropriately, please call our Supporter Services team on 0300 123 6646 to complete the transaction."
            : "We only accept donations up to £100,000 online. To donate a different amount call us on 0300 123 1022.",
          (userInput) =>
            (typeof frequency === "undefined" && formVersion === "regular") ||
            !userInput ||
            Number(userInput) <= max,
        )
        .when("$hasNudges", (hasNudges: boolean, schema) =>
          schema.required(getRequiredMessage(hasNudges)),
        ),
      otherwise: yup.string(),
    }),
  };

  return {
    fields: base,
    noSortEdges: ["amountRadioGroup", "otherAmount"],
  };
};

export const DonationAmountFormSchema = yup.object().shape({
  donationAmount: yup.object().when(
    ["frequency", "$formName", "$formVersion"],
    // @ts-ignore Yup runtime expands dependent values as positional args.
    (frequency, formName, formVersion, schema) => {
      const { fields, noSortEdges } = donationAmountSchema(
        frequency,
        formName,
        formVersion,
      );
      return schema.shape(fields, noSortEdges);
    },
  ),
  frequency: yup.string().when("$formVersion", {
    is: "regular",
    then: yup
      .string()
      .required("Please select how often you'd like to make your donation."),
    otherwise: yup.string(),
  }),
  typeRadioGroup: yup.string().when("$formVersion", {
    is: "single",
    then: yup.string().required("Please select an option to continue."),
    otherwise: yup.string(),
  }),
  motivation: yup.string(),
  otherDonationMotivation: yup
    .string()
    .trim()
    .max(100, "Please complete with a maximum of 100 characters."),
  inMemoryName: yup
    .string()
    .trim()
    .matches(commonSchema.inMemoryName.allowedCharacters.regex, {
      message: commonSchema.inMemoryName.allowedCharacters.errorMessage,
      excludeEmptyString: true,
    })
    .matches(commonSchema.inMemoryName?.minMaxLength.regex, {
      message: commonSchema.inMemoryName?.minMaxLength.errorMessage,
      excludeEmptyString: true,
    })
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^0-9‘]*$/, {
      message: commonSchema.inMemoryName.allowedCharacters.errorMessage,
      excludeEmptyString: true,
    }),
  destinationRadioGroup: yup
    .string()
    .required("Please select an option to continue"),
  restriction: yup.string(),
  emailOptIn: yup.string(),
  postOptIn: yup.string(),
  textOptIn: yup.string(),
  phoneOptIn: yup.string(),
});

export const DetailsFormSchema = yup.object().shape({
  title: yup.string(),
  forename: yup
    .string()
    .trim()
    .required(supporterSchema.firstName.isRequired.errorMessage)
    .matches(supporterSchema.firstName.allowedCharacters.regex, {
      message: supporterSchema.firstName.allowedCharacters.errorMessage,
    })
    .matches(supporterSchema.firstName.minMaxLength?.regex, {
      message: supporterSchema.firstName.minMaxLength?.errorMessage,
    })
    // @ts-expect-error
    .negativeMatch(
      supporterSchema.firstName.forbiddenSubstrings?.errorMessage,
      supporterSchema.firstName.forbiddenSubstrings?.regex,
    )
    .negativeMatch(
      supporterSchema.firstName.forbiddenSubstrings?.errorMessage,
      nameExcludedRegExp,
    )
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^‘]*$/, {
      message: supporterSchema.firstName.allowedCharacters.errorMessage,
    }),
  surname: yup
    .string()
    .trim()
    .required(supporterSchema.lastName.isRequired.errorMessage)
    .matches(supporterSchema.lastName.allowedCharacters.regex, {
      message: supporterSchema.lastName.allowedCharacters.errorMessage,
    })
    .matches(supporterSchema.lastName.minMaxLength?.regex, {
      message: supporterSchema.lastName.minMaxLength?.errorMessage,
    })
    // @ts-expect-error
    .negativeMatch(
      supporterSchema.lastName.forbiddenSubstrings?.errorMessage,
      supporterSchema.lastName.forbiddenSubstrings?.regex,
    )
    .negativeMatch(
      supporterSchema.lastName.forbiddenSubstrings?.errorMessage,
      nameExcludedRegExp,
    )
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^‘]*$/, {
      message: supporterSchema.lastName.allowedCharacters.errorMessage,
    }),
  emailAddress: yup
    .string()
    .trim()
    .required(supporterSchema.emailAddress.isRequired.errorMessage)
    .email(supporterSchema.emailAddress.allowedCharacters.errorMessage) // TO-DO: remove when moved to personhub
    .matches(supporterSchema.emailAddress.allowedCharacters.regex, {
      message: supporterSchema.emailAddress.allowedCharacters.errorMessage,
    })
    // TO-DO: to remove when moved over to personhub
    // @ts-expect-error
    .negativeMatch(
      supporterSchema.emailAddress.allowedCharacters.errorMessage,
      extraEmailRegExp,
    )
    .matches(supporterSchema.emailAddress.minMaxLength?.regex, {
      message: supporterSchema.emailAddress.minMaxLength?.errorMessage,
    })
    // TO-DO: to remove when moved over to personhub
    .max(100, supporterSchema.emailAddress.minMaxLength?.errorMessage),
  phoneNumber: yup
    .string()
    .trim()
    .matches(phoneRegExp, {
      message: "Please enter a valid phone number so we can contact you.",
      excludeEmptyString: true,
    })
    .when(["textOptIn", "phoneOptIn"], {
      is: (textOptIn: string, phoneOptIn: string) =>
        phoneOptIn === "yes" || textOptIn === "yes",
      then: (schema) =>
        schema.when(["$formVersion", "$formName"], {
          is: (formVersion: string, formName: string) =>
            formVersion === "single" && formName === "support-us",
          then: yup
            .string()
            .trim()
            .required(
              'Phone number is required because staying in touch by phone or text message is set to "Yes".',
            ),
          otherwise: yup
            .string()
            .trim()
            .required(
              "Please enter a valid phone number so we can contact you.",
            ),
        }),
      otherwise: (schema) => schema,
    }),
  addressLine1: yup
    .string()
    .trim()
    .required(supporterSchema.addressLine1.isRequired.errorMessage)
    .matches(supporterSchema.addressLine1.allowedCharacters.regex, {
      message: supporterSchema.addressLine1.allowedCharacters.errorMessage,
    })
    .matches(supporterSchema.addressLine1.minMaxLength?.regex, {
      message: supporterSchema.addressLine1.minMaxLength?.errorMessage,
    })
    // @ts-expect-error
    .notOnlyNum(
      "Please complete address line 1 including your house number and street name.",
    )
    .equalsOtherField("Town name should not appear in address line 1.", "city")
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^‘’]*$/, {
      message: supporterSchema.addressLine1.allowedCharacters.errorMessage,
    }),
  addressSelection: yup.string().when("addressSearchStatus", {
    is: "search",
    then: yup.string().required("Please select an address."),
  }),
  addressSearchStatus: yup.string(),
  addressLine2: yup
    .string()
    .trim()
    .matches(supporterSchema.addressLine2.allowedCharacters.regex, {
      message: supporterSchema.addressLine2.allowedCharacters.errorMessage,
      excludeEmptyString: true,
    })
    .matches(supporterSchema.addressLine2?.minMaxLength.regex, {
      message: supporterSchema.addressLine2?.minMaxLength.errorMessage,
      excludeEmptyString: true,
    })
    // @ts-expect-error
    .equalsOtherField("Town name should not appear in address line 2.", "city")
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^‘’]*$/, {
      message: supporterSchema.addressLine2.allowedCharacters.errorMessage,
    }),
  addressLine3: yup
    .string()
    .trim()
    .matches(supporterSchema.addressLine3.allowedCharacters.regex, {
      message: supporterSchema.addressLine3.allowedCharacters.errorMessage,
      excludeEmptyString: true,
    })
    .matches(supporterSchema.addressLine3?.minMaxLength.regex, {
      message: supporterSchema.addressLine3?.minMaxLength.errorMessage,
      excludeEmptyString: true,
    })
    // @ts-expect-error
    .equalsOtherField("Town name should not appear in address line 3.", "city")
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^‘’]*$/, {
      message: supporterSchema.addressLine3.allowedCharacters.errorMessage,
    }),
  city: yup
    .string()
    .trim()
    .required(supporterSchema.town.isRequired.errorMessage)
    .matches(supporterSchema.town.allowedCharacters.regex, {
      message: "Please only use letters and the following: - ' / .", // TO-DO: update when moved to personhub
    })
    .matches(supporterSchema.town.minMaxLength?.regex, {
      message: supporterSchema.town.minMaxLength?.errorMessage,
    })
    // TO-DO: to remove when moved over to personhub
    .matches(/^[^0-9&‘’]*$/, {
      message: "Please only use letters and the following: - ' / .",
    }),
  postalCode: yup
    .string()
    .trim()
    .required(supporterSchema.postcodeUK.isRequired.errorMessage)
    .when("country", {
      is: (country: string) => isUkCountry(country),
      then: yup
        .string()
        .trim()
        // @ts-expect-error
        .minMax("The postcode must be between 5 and 8 characters.", 5, 8),
      otherwise: yup
        .string()
        .trim()
        .matches(supporterSchema.postcodeIntl.minMaxLength?.regex, {
          message: supporterSchema.postcodeIntl.minMaxLength?.errorMessage,
        }),
    })
    .when("country", {
      is: (country: string) => isUkCountry(country),
      then: yup.string().trim().matches(postcodeUkRegExp, {
        message: "Please enter a valid postcode.",
      }),
      otherwise: yup.string().trim().matches(postcodeIntlRegExp, {
        message: "Please enter a valid postcode.",
      }),
    }),
  country: yup.string().required(),
  validationStatus: yup.string().required(),
});

export const PaymentFormSchema = yup.object().shape({
  paymentRadioGroup: yup.string().when("$formVersion", {
    is: "single",
    then: yup.string().required("Please select a payment method to continue."),
    otherwise: yup.string(),
  }),
  cardholderName: yup.string().when(["$formVersion", "paymentRadioGroup"], {
    is: (formVersion: string, paymentRadioGroup: string) =>
      formVersion === "single" && paymentRadioGroup === "bt",
    then: yup
      .string()
      .trim()
      .required(
        "Please enter the name of the cardholder as it appears on the card.",
      )
      // @ts-expect-error
      .minMax(
        "Please enter your cardholder name between 2 - 175 characters.",
        2,
        175,
      )
      .matches(nameRegExp, {
        message: "Please only use letters and hyphens.",
      })
      .negativeMatch(
        "Please enter your cardholder name, this cannot contain the word 'and'.",
        andRegExp,
      )
      .negativeMatch(
        "Please enter a valid cardholder name.",
        cardholderNameExcludedRegExp,
      ),
    otherwise: yup.string(),
  }),
  giftAid: yup.boolean(),
  billingAddress: yup.array().nullable(),
  billingAddressLine1: yup.string().when("billingAddress", {
    is: (val: string) => val && val.includes("yes"),
    then: yup
      .string()
      .trim()
      .required(supporterSchema.addressLine1.isRequired.errorMessage)
      .matches(supporterSchema.addressLine1.allowedCharacters.regex, {
        message: supporterSchema.addressLine1.allowedCharacters.errorMessage,
      })
      .matches(supporterSchema.addressLine1.minMaxLength?.regex, {
        message: supporterSchema.addressLine1.minMaxLength?.errorMessage,
      })
      // @ts-expect-error
      .notOnlyNum(
        "Please complete address line 1 including your house number and street name.",
      )
      .equalsOtherField(
        "Town name should not appear in address line 1.",
        "billingCity",
      ),
    otherwise: yup.string(),
  }),
  billingAddressSelection: yup.string().when("billingAddressSearchStatus", {
    is: "search",
    then: yup.string().required("Please select an address."),
  }),
  billingAddressSearchStatus: yup.string(),
  billingAddressLine2: yup
    .string()
    .trim()
    .matches(supporterSchema.addressLine2.allowedCharacters.regex, {
      message: supporterSchema.addressLine2.allowedCharacters.errorMessage,
      excludeEmptyString: true,
    })
    .matches(supporterSchema.addressLine2.minMaxLength?.regex, {
      message: supporterSchema.addressLine2.minMaxLength?.errorMessage,
      excludeEmptyString: true,
    })
    // @ts-expect-error
    .equalsOtherField(
      "Town name should not appear in address line 2.",
      "billingCity",
    ),
  billingAddressLine3: yup
    .string()
    .trim()
    .matches(supporterSchema.addressLine3.allowedCharacters.regex, {
      message: supporterSchema.addressLine3.allowedCharacters.errorMessage,
      excludeEmptyString: true,
    })
    .matches(supporterSchema.addressLine3.minMaxLength?.regex, {
      message: supporterSchema.addressLine3.minMaxLength?.errorMessage,
      excludeEmptyString: true,
    })
    // @ts-expect-error
    .equalsOtherField(
      "Town name should not appear in address line 3.",
      "billingCity",
    ),
  billingCity: yup.string().when("billingAddress", {
    is: (val: string) => val && val.includes("yes"),
    then: yup
      .string()
      .trim()
      .required(supporterSchema.town.isRequired.errorMessage)
      .matches(supporterSchema.town.allowedCharacters.regex, {
        message: supporterSchema.town.allowedCharacters.errorMessage,
      })
      .matches(supporterSchema.town.minMaxLength?.regex, {
        message: supporterSchema.town.minMaxLength?.errorMessage,
      }),
    otherwise: yup.string(),
  }),
  billingPostalCode: yup
    .string()
    .trim()
    .when("billingAddress", {
      is: (val: string) => val && val.includes("yes"),
      then: yup
        .string()
        .required(supporterSchema.postcodeUK.isRequired.errorMessage)
        .when("billingCountry", {
          is: (country: string) => isUkCountry(country),
          then: yup
            .string()
            .trim()
            // @ts-expect-error
            .minMax("The postcode must be between 5 and 8 characters.", 5, 8),
          otherwise: yup
            .string()
            .trim()
            .matches(supporterSchema.postcodeIntl.minMaxLength?.regex, {
              message: supporterSchema.postcodeIntl.minMaxLength?.errorMessage,
            }),
        })
        .when("billingCountry", {
          is: (country: string) => isUkCountry(country),
          then: yup.string().trim().matches(postcodeUkRegExp, {
            message: "Please enter a valid postcode.",
          }),
          otherwise: yup.string().trim().matches(postcodeIntlRegExp, {
            message: "Please enter a valid postcode.",
          }),
        }),
      otherwise: yup.string(),
    }),
  billingCountry: yup.string(),
});

export const RegularPaymentFormSchema = yup.object().shape({
  accountName: yup.string().when("$formVersion", {
    is: "regular",
    then: yup
      .string()
      .required(directDebitSchema.accountHolderName.isRequired.errorMessage)
      .matches(directDebitSchema.accountHolderName.allowedCharacters.regex, {
        message:
          directDebitSchema.accountHolderName.allowedCharacters.errorMessage,
      })
      // TO-DO: to remove/change when moved over to personhub
      // @ts-expect-error
      .minMax(
        "Please enter the account holder's name between 2 - 50 characters.",
        2,
        50,
      )
      .matches(/^[^0-9‘]*$/, {
        message:
          directDebitSchema.accountHolderName.allowedCharacters.errorMessage,
        excludeEmptyString: true,
      })
      .min(2, "Account name must be at least 2 characters long."),
    otherwise: yup.string(),
  }),
  accountNumber: yup.string().when("$formVersion", {
    is: "regular",
    then: yup
      .string()
      .required(directDebitSchema.bankAccountNumber.isRequired.errorMessage)
      .matches(directDebitSchema.bankAccountNumber.allowedCharacters.regex, {
        message:
          directDebitSchema.bankAccountNumber.allowedCharacters.errorMessage,
      })
      .matches(directDebitSchema.bankAccountNumber.minMaxLength?.regex, {
        message: directDebitSchema.bankAccountNumber.minMaxLength?.errorMessage,
      }),
    otherwise: yup.string(),
  }),
  sortCode: yup.string().when("$formVersion", {
    is: "regular",
    then: yup
      .string()
      .required(directDebitSchema.bankSortCode.isRequired.errorMessage)
      .matches(sortCodeRegExp, {
        message: directDebitSchema.bankSortCode.allowedCharacters.errorMessage,
      })
      .test(
        "minmax",
        directDebitSchema.bankSortCode.minMaxLength?.errorMessage,
        (userInput) =>
          !userInput ||
          directDebitSchema.bankSortCode.minMaxLength?.regex.test(
            userInput.replace(/[- ]/g, ""),
          ),
      ),

    otherwise: yup.string(),
  }),
  donationDate: yup.string().when("$formVersion", {
    is: "regular",
    then: yup
      .string()
      .required("Please select the date you'd like to make your donation on."),
    otherwise: yup.string(),
  }),
});

export const DonationAndDetailFormSchema =
  DonationAmountFormSchema.concat(DetailsFormSchema);

export const FormSchema = PaymentFormSchema.concat(DonationAndDetailFormSchema);

export const RegularFormSchema = RegularPaymentFormSchema.concat(
  DonationAndDetailFormSchema,
);

export const initialDonationAmountFormValues = {
  donationAmount: {
    amountRadioGroup: "",
    otherAmount: "",
  },
  typeRadioGroup: "",
  frequency: "",
  motivation: "",
  otherDonationMotivation: "",
  inMemoryName: "",
  destinationRadioGroup: "greatest",
  restriction: "",
  emailOptIn: "",
  postOptIn: "yes",
  textOptIn: "",
  phoneOptIn: "yes",
};

export const initialDetailsFormValues = {
  title: "",
  forename: "",
  surname: "",
  emailAddress: "",
  phoneNumber: "",
  addressLine1: "",
  addressSelection: "",
  addressSearchStatus: "",
  addressLine2: "",
  addressLine3: "",
  city: "",
  postalCode: "",
  country: "United Kingdom",
  validationStatus: "NV",
};

export const initialPaymentFormValues = {
  cardholderName: "",
  paymentRadioGroup: "",
  giftAid: false,
  billingAddress: "",
  billingAddressLine1: "",
  billingAddressSelection: "",
  billingAddressSearchStatus: "",
  billingAddressLine2: "",
  billingAddressLine3: "",
  billingCity: "",
  billingPostalCode: "",
  billingCountry: "United Kingdom",
};

export const initialRegularPaymentFormValues = {
  accountName: "",
  accountNumber: "",
  sortCode: "",
  donationDate: "",
};

export const initialFormValues = {
  ...initialDonationAmountFormValues,
  ...initialDetailsFormValues,
  ...initialPaymentFormValues,
  ...initialRegularPaymentFormValues,
};

export type FormSchemaType = typeof initialFormValues;
